import { useCallback, useMemo } from 'react';
import { useForm, UseFormReset, UseFormReturn } from 'react-hook-form';

import { RdProject } from '@neo.tax/rd/hooks';
import { ProjectDisallowance } from '@neo.tax/shared';

import type { RdProjectDisallowancesForm } from '../rd-project-disallowances-modal.types';
import { reduceRdProjectDisallowances } from '../utils/rd-project-disallowances-modal.utils';

const makeDefaultFormValues = (projectDisallowances: ProjectDisallowance[] | null): RdProjectDisallowancesForm => {
  const disallowances = reduceRdProjectDisallowances((disallowance) =>
    projectDisallowances === null ? false : projectDisallowances.includes(disallowance),
  );
  // Don't automatically set `noneOfTheAbove` to true if the disallowances set is null; force the user to select "None of the above" or at least one disallowance
  const noneOfTheAbove = projectDisallowances === null ? false : projectDisallowances.length === 0;

  return {
    ...disallowances,
    noneOfTheAbove,
  };
};

interface UseRdProjectDisallowanceFormProps {
  rdProject: RdProject;
}

export const useRdProjectDisallowanceForm = ({
  rdProject,
}: UseRdProjectDisallowanceFormProps): UseFormReturn<RdProjectDisallowancesForm> => {
  const disallowances = useMemo(() => rdProject.disallowances ?? null, [rdProject.disallowances]);

  const { reset, ...form } = useForm<RdProjectDisallowancesForm>({
    mode: 'onSubmit',
    defaultValues: makeDefaultFormValues(disallowances),
  });

  const resetForm: UseFormReset<RdProjectDisallowancesForm> = useCallback(
    (values, keepStateOptions): void => reset(values ?? makeDefaultFormValues(disallowances), keepStateOptions),
    [disallowances, reset],
  );

  return {
    ...form,
    reset: resetForm,
  };
};
